import { ref, readonly, computed, ComputedRef } from 'vue'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import relativeTime from 'dayjs/plugin/relativeTime'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import customParseFormat from 'dayjs/plugin/customParseFormat' // 用於支持日期格式驗證

// 載入必要的插件
dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(advancedFormat)
dayjs.extend(relativeTime)
dayjs.extend(isSameOrAfter)
dayjs.extend(isSameOrBefore)
dayjs.extend(customParseFormat)


// 獲取用戶本地時區
const getUserTimezone = () => dayjs.tz.guess()

// 預設時區 - 使用瀏覽器本地時區作為預設值
const currentTimezone = ref(getUserTimezone() || 'UTC')

// 創建一個封裝後的 dayjs 函數
const dayjsInstance = (date?: string | Date | number) => {
 if (typeof date === "string") {
  if (
   date.length > 0 &&
   date[date.length - 1] !== "Z" &&
   /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/.test(date) // 檢查是否符合 ISO 8601 格式但沒有 Z
  ) {
   date = date + "Z";
  }
 }
 return dayjs(date).tz(currentTimezone.value)
}

// 添加 localDayjs 函數，對應原本的 localMoment 功能
const localDayjs = (
 date: string | number | Date = new Date(),
 timeZone?: string
) => {
 // 處理字符串日期，如果是有效的 ISO 格式且末尾沒有 Z，則添加 Z
 if (typeof date === "string") {
  if (
   date.length > 0 &&
   date[date.length - 1] !== "Z" &&
   /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/.test(date) // 檢查是否符合 ISO 8601 格式但沒有 Z
  ) {
   date = date + "Z";
  }
 }

 // 獲取時區，優先使用傳入的時區，其次使用瀏覽器時區，最後使用 UTC
 const tz = timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

 // 先轉換為 UTC，再轉換到目標時區
 return dayjs.utc(date).tz(tz);
}

// 提供更改時區的方法
const setTimezone = (timezone: string) => {
 if (isValidTimezone(timezone)) {
  currentTimezone.value = timezone
  // 觸發更新
  updateNow()
 } else {
  console.error(`Invalid timezone: ${timezone}`)
 }
}

// 檢查時區是否有效的方法
const isValidTimezone = (timezone: string): boolean => {
 try {
  dayjs().tz(timezone)
  return true
 } catch (error) {
  return false
 }
}

// 獲取當前時區
const getTimezone = () => readonly(currentTimezone)

// 當前時間的 ref
const now = ref(dayjsInstance())

// 更新當前時間
const updateNow = () => {
 now.value = dayjsInstance()
}

// 格式化日期的便捷方法
const formatDate = (
 date?: string | Date | number,
 format = 'YYYY-MM-DD HH:mm:ss'
) => {
 return dayjsInstance(date).format(format)
}

// 創建一個響應式的格式化當前時間
const formattedNow = (format = 'YYYY-MM-DD HH:mm:ss'): ComputedRef<string> => {
 // 使用計算屬性，依賴於 now 和 currentTimezone
 return computed(() => {
  return now.value.format(format)
 })
}

// 自動更新時間的方法
const startAutoUpdate = (intervalMs = 1000) => {
 const intervalId = setInterval(() => {
  updateNow()
 }, intervalMs)

 // 返回停止函數
 return () => clearInterval(intervalId)
}

// 預定義的時區列表
const getAllTimezones = (): string[] => {
 return [
  // 亞洲
  'Asia/Taipei', 'Asia/Tokyo', 'Asia/Shanghai', 'Asia/Hong_Kong', 'Asia/Macau', 'Asia/Singapore',
  'Asia/Seoul', 'Asia/Kuala_Lumpur', 'Asia/Manila', 'Asia/Bangkok', 'Asia/Jakarta', 'Asia/Ho_Chi_Minh',
  'Asia/Phnom_Penh', 'Asia/Vientiane', 'Asia/Yangon', 'Asia/Dhaka', 'Asia/Colombo', 'Asia/Kathmandu',
  'Asia/Karachi', 'Asia/Kolkata', 'Asia/Dubai', 'Asia/Riyadh', 'Asia/Jerusalem', 'Asia/Baghdad',
  'Asia/Tehran', 'Asia/Almaty', 'Asia/Tashkent', 'Asia/Yekaterinburg', 'Asia/Novosibirsk',
  'Asia/Irkutsk', 'Asia/Yakutsk', 'Asia/Vladivostok', 'Asia/Magadan', 'Asia/Kamchatka',

  // 歐洲
  'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Madrid', 'Europe/Rome', 'Europe/Amsterdam',
  'Europe/Brussels', 'Europe/Vienna', 'Europe/Zurich', 'Europe/Prague', 'Europe/Copenhagen',
  'Europe/Stockholm', 'Europe/Oslo', 'Europe/Helsinki', 'Europe/Warsaw', 'Europe/Budapest',
  'Europe/Athens', 'Europe/Istanbul', 'Europe/Moscow', 'Europe/Dublin', 'Europe/Lisbon',
  'Europe/Bucharest', 'Europe/Kiev', 'Europe/Sofia', 'Europe/Belgrade', 'Europe/Minsk',

  // 北美洲
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'America/Anchorage',
  'America/Toronto', 'America/Vancouver', 'America/Edmonton', 'America/Halifax', 'America/St_Johns',
  'America/Mexico_City', 'America/Phoenix', 'America/Honolulu', 'America/Juneau', 'America/Indianapolis',
  'America/Detroit', 'America/Winnipeg', 'America/Montreal', 'America/Havana', 'America/Jamaica',

  // 南美洲
  'America/Sao_Paulo', 'America/Buenos_Aires', 'America/Santiago', 'America/Lima', 'America/Bogota',
  'America/Caracas', 'America/La_Paz', 'America/Montevideo', 'America/Asuncion', 'America/Guayaquil',

  // 澳洲與大洋洲
  'Australia/Sydney', 'Australia/Melbourne', 'Australia/Brisbane', 'Australia/Perth', 'Australia/Adelaide',
  'Australia/Darwin', 'Australia/Hobart', 'Pacific/Auckland', 'Pacific/Fiji', 'Pacific/Honolulu',
  'Pacific/Guam', 'Pacific/Tahiti', 'Pacific/Noumea', 'Pacific/Port_Moresby',

  // 非洲
  'Africa/Cairo', 'Africa/Johannesburg', 'Africa/Lagos', 'Africa/Nairobi', 'Africa/Casablanca',
  'Africa/Tunis', 'Africa/Algiers', 'Africa/Khartoum', 'Africa/Accra', 'Africa/Addis_Ababa',
  'Africa/Dar_es_Salaam', 'Africa/Kampala',

  // 南極洲
  'Antarctica/Casey', 'Antarctica/Davis', 'Antarctica/Mawson', 'Antarctica/McMurdo', 'Antarctica/Rothera',
  'Antarctica/Syowa', 'Antarctica/Troll', 'Antarctica/Vostok',

  // UTC
  'UTC'
 ];
}

// 獲取帶有時區偏移的時區選項
const getTimezoneOptions = () => {
 const timezones = getAllTimezones();
 return timezones.map(tz => {
  try {
   const offset = dayjs().tz(tz).format('Z');  // 獲取時區偏移，如 "+08:00"
   const parts = tz.split('/');
   const region = parts[0];
   const city = parts.length > 1 ? parts.slice(1).join('/') : tz;

   // 格式化城市名稱，將底線替換為空格
   const formattedCity = city.replace(/_/g, ' ');

   return {
    label: `${formattedCity} (${region !== city ? region + ', ' : ''}UTC${offset})`,
    value: tz,
    // 添加額外資訊以支援搜尋
    region: region,
    city: formattedCity,
    offset: offset,
    // 完整的搜尋文字
    searchText: `${formattedCity} ${region} UTC${offset}`.toLowerCase()
   };
  } catch (error) {
   // 如果處理特定時區出錯，返回原始時區名稱
   return {
    label: tz,
    value: tz,
    region: '',
    city: tz,
    offset: '',
    searchText: tz.toLowerCase()
   };
  }
 });
}

// 提供所有 dayjs 的功能
Object.assign(dayjsInstance, dayjs)

export function useDayjs() {
 return {
  dayjs: dayjsInstance,
  localDayjs,
  now,
  formattedNow,
  formatDate,
  setTimezone,
  getTimezone,
  startAutoUpdate,
  getAllTimezones,
  getTimezoneOptions,
  getUserTimezone,
  updateNow // 導出更新方法
 }
}
